import { useState, useLayoutEffect, useEffect } from 'react'

import { ImageBlobPaginatedList, ImagesImageIdBlobsBlobIdDeleteRequest } from '@nativewaves/nw-cloud-sdk/content'
import { useQuery, useInfiniteQuery, useMutationState, InfiniteData, useQueryClient } from '@tanstack/react-query'
import { VirtualItem } from '@tanstack/react-virtual'

import { imageBlobsOverviewRoute } from 'pages/NWPlatform/Workspaces/Workspace/Images/Image/Blobs/Routing'
import { imageObjectQueryKeys, workspaceQueryKeys } from 'services/queryKeys'
import { RefetchIntervals } from 'types/query'
import { mergeBatchIntoSource } from 'utils'

type UseAsyncContentCaching = {
  virtualItems?: VirtualItem[]
  setLoadingRows: (rows: string[]) => void
}

export const useAsyncContentCaching = ({ virtualItems, setLoadingRows }: UseAsyncContentCaching) => {
  const queryClient = useQueryClient()

  const { workspaceId, imageId } = imageBlobsOverviewRoute.useParams()

  const workspaceQuery = useQuery(workspaceQueryKeys.detail({ workspaceId }))

  const imageBlobsQuery = useInfiniteQuery({
    ...imageObjectQueryKeys.blobList({
      imageId,
    }),
    enabled: workspaceQuery.isSuccess,
    select: (data) => ({
      ...data,
      flat: data?.pages.flatMap((page) => page.items),
    }),
    staleTime: Infinity,
    notifyOnChangeProps: 'all',
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  const [batchGetEnabled, setBatchGetEnabled] = useState(imageBlobsQuery.isSuccess)

  const pendingImageBlobs = useMutationState({
    filters: { mutationKey: ['content', 'image-blob', 'DELETE'], status: 'pending' },
    select: (mutation) => mutation.state.variables as ImagesImageIdBlobsBlobIdDeleteRequest,
  })

  const imageBlobBatchQuery = useQuery({
    ...imageObjectQueryKeys.blobBatchGet(
      { imageId },
      () => virtualItems?.map((virtualItem) => imageBlobsQuery.data?.flat[virtualItem.index]?.id) as string[],
    ),
    enabled: batchGetEnabled,
    refetchInterval: RefetchIntervals.NORMAL,
    staleTime: 0,
    retry: false,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  })

  useLayoutEffect(() => {
    if (imageBlobsQuery.isSuccess && imageBlobBatchQuery.isSuccess) {
      queryClient.setQueriesData<InfiniteData<ImageBlobPaginatedList>>(
        { queryKey: imageObjectQueryKeys.blobLists() },
        (old) => {
          if (old) {
            return {
              ...old,
              pages:
                old.pages.map((page) => ({
                  ...page,
                  items: mergeBatchIntoSource(
                    page.items,
                    imageBlobBatchQuery.data.items,
                    new Set(imageBlobBatchQuery.data.notFoundIds),
                  ),
                })) ?? [],
            }
          }
          return old
        },
      )
    }
  }, [
    imageBlobBatchQuery.data?.items,
    imageBlobBatchQuery.data?.notFoundIds,
    imageBlobBatchQuery.isSuccess,
    imageBlobsQuery.isSuccess,
    queryClient,
  ])

  useLayoutEffect(() => {
    setLoadingRows([...new Set([...pendingImageBlobs.map((task) => task.blobId)])])
  }, [pendingImageBlobs, setLoadingRows])

  useEffect(() => {
    if (imageBlobsQuery.isSuccess) {
      if (!batchGetEnabled) {
        const timeoutHandle = setTimeout(() => {
          setBatchGetEnabled(true)
          clearTimeout(timeoutHandle)
        }, 5000)
      }
    }
  }, [batchGetEnabled, imageBlobsQuery.isSuccess])

  return {
    workspaceQuery,
    imageBlobsQuery,
    setBatchGetEnabled,
  }
}
